import { useCallback, useEffect, useState } from 'react';
import { AppShell } from './AppShell';
import { fetchSession, logout, onSignedOut, type ConsoleSession } from './lib/api';
import { takeReturnHash } from './lib/return-hash';
import { SessionProvider, sessionFor } from './lib/session';
import { useHashRoute } from './lib/use-hash-route';
import { useTheme } from './lib/theme';
import { Processing } from './routes/Processing';
import { Documents } from './routes/Documents';
import { DocRecord } from './routes/DocRecord';
import { NotProcessed } from './routes/NotProcessed';
import { ExtractionRules } from './routes/ExtractionRules';
import { Login } from './routes/Login';
import { Settings } from './routes/Settings';
import { TestExtraction } from './routes/TestExtraction';
import { NotFound } from './routes/NotFound';

/**
 * The site map's six routes, matched off `useHashRoute`'s segments.
 * `#processing` and `#documents` are fully functional (OPS-01 and OPS-02); the remaining three
 * render a real, deep-linkable stub a later row fills in — see each route file's own header
 * comment for which.
 */
export function App() {
  const { segments, query } = useHashRoute();
  const searchQuery = query.get('q') ?? '';
  const [session, setSession] = useState<'checking' | 'in' | 'out'>('checking');
  const [user, setUser] = useState<ConsoleSession | null>(null);
  // Here, not in `AppShell`, so the sign-in page follows the system theme too.
  const { theme, toggle: toggleTheme } = useTheme();

  // Ask the backend at start; a `401` from any later request (`lib/api.ts`) signs the app out.
  // A sign-in through Keycloak ends on `/` with no hash. `Login` kept the hash the person was on, so
  // it goes back now. It sets `location.hash`, never `location.href`: the value came from the
  // address bar, so it can only change the route, not leave the app.
  useEffect(() => {
    fetchSession().then(
      (signedIn) => {
        setUser(signedIn);
        setSession(signedIn ? 'in' : 'out');
        if (!signedIn) return;
        const returnHash = takeReturnHash();
        if (returnHash && ['', '#'].includes(window.location.hash)) window.location.hash = returnHash;
      },
      () => setSession('out'),
    );
    return onSignedOut(() => setSession('out'));
  }, []);

  const signOut = useCallback(() => {
    // When it navigated, the page is going to Keycloak; the sign-in page shows only if it did not.
    void logout().then((navigated) => {
      if (!navigated) setSession('out');
    });
  }, []);

  if (session === 'checking') return null;
  if (session === 'out' || !user) return <Login />;

  return (
    <SessionProvider
      session={sessionFor(user.username, user.role, { superAdmin: user.superAdmin, userAdminUrl: user.userAdminUrl })}
    >
      <AppShell
        onSignOut={signOut}
        theme={theme}
        onToggleTheme={toggleTheme}
        routeKey={`${segments.join('/')}?${query.toString()}`}
        searchQuery={searchQuery}
        // `route` is the first segment only — what the header highlights. `routeKey` stays the
        // whole route because focus has to move again when only the query changes.
        route={segments[0] || 'processing'}
      >
        {renderRoute(segments, searchQuery, query)}
      </AppShell>
    </SessionProvider>
  );
}

function renderRoute(segments: string[], searchQuery: string, query: URLSearchParams) {
  const [first, second] = segments;
  switch (first) {
    case '':
    case 'processing':
      return <Processing fragment={second === 'why' ? second : undefined} />;
    case 'documents':
      return <Documents query={searchQuery} />;
    case 'doc': {
      // #doc/<id> — `id` is `<type>/<stem>` and may itself contain a `/`, so everything after
      // the first segment is the id, not just `segments[1]`.
      // The id is also the `key`, so another document starts clean: no Run again notice and a closed
      // raw-file view from the page before.
      const id = segments.slice(1).join('/');
      return <DocRecord key={id} id={id} />;
    }
    case 'not-processed':
      return <NotProcessed failureClass={query.get('failureClass') ?? undefined} />;
    case 'extraction-rules':
      return <ExtractionRules />;
    case 'test-extraction':
      return <TestExtraction />;
    case 'settings':
      return <Settings />;
    default:
      return <NotFound path={first} />;
  }
}
